import parseHumanDuration from 'parse-duration';
import { z } from 'zod';

const nonEmptyString = z
  .string()
  .trim()
  .min(1, { message: 'errors.invalid_type_received_undefined' });

export const lineItemSchema = z.object({
  id: z.number(),
  workRequestId: z.number(),
  name: z.string().nonempty({ message: 'line_item_required' }),
  description: z
    .string()
    .max(255, { message: 'text_too_long' })
    .optional()
    .default(''),
  duration: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value) return true;
        try {
          // This function has an incorrect type definition
          /**
           * it should return number | null
           * `null` if the value is not a valid duration
           * and a `number` if it is
           */
          const parsed: number | null = parseHumanDuration(value);
          if (typeof parsed === 'number') {
            return true;
          }
          return false;
        } catch {
          return false;
        }
      },
      {
        params: { i18n: 'invalid_duration' },
      },
    )
    .default('0'),
  quantity: z.coerce.number({
    invalid_type_error: 'errors.invalid_type_received_undefined',
  }),
  unitPrice: z.coerce.number({ invalid_type_error: 'price_required' }),
  totalPrice: z.coerce.number({
    invalid_type_error: 'errors.invalid_type_received_undefined',
  }),
  isTaxable: z.boolean(),
  actualDuration: z.string().nullable().default(''),
  actualQuantity: z.number().nullable(),
  actualTotalPrice: z.number().nullable(),
  type: z.union([z.literal('INVENTORY'), z.literal('SERVICE')]),
  workTypeId: z.number().optional().nullable(),
});

export const computationsSchema = z.object({
  subTotal: z.number(),
  taxableAmount: z.number(),
  taxPercentage: z.number({ invalid_type_error: 'tax_percentage_required' }),
  taxedAmount: z.number(),
  itemCost: z.number(),
  total: z.number().default(0),
  estimatedHours: z.number(),
  requireDeposit: z.boolean().optional().default(false),
  depositPercentage: z.number(),
  depositAmount: z.number().optional(),
});

export const customerSchema = z.object({
  id: z.number(),
  address: z.object({
    id: z.number(),
    street: nonEmptyString,
    street2: z.string().trim().nullable().optional().default(''),
    city: nonEmptyString,
    state: nonEmptyString,
    postalCode: nonEmptyString,
    country: nonEmptyString,
    lng: z.number().refine(
      (val) => {
        return val !== 0;
      },
      { message: 'No coordinates set' },
    ),
    lat: z
      .number()
      .refine((val) => val !== 0, { message: 'No coordinates set' }),
    timeZone: z.string().trim().nullable().optional().default(''),
    isVerified: z.boolean().optional().default(false),
    isVerifiying: z.boolean().optional(),
  }),
  firstName: nonEmptyString,
  lastName: nonEmptyString,
  businessName: z.string().optional().nullish(),
  email: nonEmptyString,
  phone: nonEmptyString,
});

export const addressSchema = z.object({
  street: nonEmptyString,
  street2: z.string().trim().nullable().optional().default(''),
  city: nonEmptyString,
  state: nonEmptyString,
  postalCode: nonEmptyString,
  country: nonEmptyString,
  lng: z.number().refine(
    (val) => {
      return val !== 0;
    },
    { message: 'No coordinates set' },
  ),
  lat: z.number().refine((val) => val !== 0, { message: 'No coordinates set' }),
  timeZone: z.string().trim().nullable().optional().default(''),
});

export const invoiceBaseSchema = z.object({
  images: z.instanceof(FileList, { message: 'Required' }).optional(),
  items: lineItemSchema.array(),
  notes: z.string().optional(),
  description: z.string().trim().optional(),
  poNumber: z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((val) => (val === '' ? null : val)),
  soNumber: z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((val) => (val === '' ? null : val)),
  shipName: z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((val) => (val === '' ? null : val)),
  serviceLocation: addressSchema.extend({
    id: z.number(),
    serviceLocationId: z.number(),
  }),
  pickupLocation: addressSchema.extend({
    id: z.number(),
    serviceLocationId: z.number(),
  }),
  billTo: customerSchema.extend({
    id: z.number(),
  }),
  computations: computationsSchema.extend({
    /**
     * This will hold the id of the estimate/invoice
     * that was created(POST:/invoice or /estimate)
     */
    createdInvoiceId: z.number().optional().default(0),
  }),
  dueDate: z.string().optional(),
});

export const estimateBaseSchema = z.object({
  images: z.instanceof(FileList, { message: 'Required' }).optional(),
  items: lineItemSchema.array(),
  notes: z.string().optional(),
  description: z.string().trim().optional().default(''),
  poNumber: z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((val) => (val === '' ? null : val)),
  soNumber: z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((val) => (val === '' ? null : val)),
  shipName: z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((val) => (val === '' ? null : val)),
  serviceLocation: addressSchema.extend({
    id: z.number(),
    serviceLocationId: z.number(),
  }),
  pickupLocation: addressSchema
    .extend({
      id: z.number(),
      serviceLocationId: z.number(),
    })
    .optional(),
  billTo: customerSchema.extend({
    id: z.number(),
  }),
  computations: computationsSchema.extend({
    createdEstimateId: z.number().optional(),
  }),
});

export const jobBaseSchema = z.object({
  images: z.instanceof(FileList, { message: 'Required' }).optional(),
  items: lineItemSchema.array(),
  notes: z.string().optional(),
  description: z.string().trim().optional().default(''),
  poNumber: z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((val) => (val === '' ? null : val)),
  soNumber: z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((val) => (val === '' ? null : val)),
  shipName: z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((val) => (val === '' ? null : val)),
  serviceLocation: addressSchema.extend({
    id: z.number(),
    serviceLocationId: z.number(),
  }),
  pickupLocation: addressSchema
    .extend({
      id: z.number(),
      serviceLocationId: z.number(),
    })
    .optional(),
  billTo: customerSchema.extend({
    id: z.number(),
  }),
});

export type EstimateFormFields = z.infer<typeof estimateBaseSchema>;
export type InvoiceFormFields = z.infer<typeof invoiceBaseSchema>;
export type JobFormFields = z.infer<typeof jobBaseSchema>;

export const schema = invoiceBaseSchema;
